import { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  Button,
  FormControl,
  FilledInput,
  IconButton,
  InputLabel,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import request from "../../../utilities/requests";
import { toast } from "../../../utilities/SnackBarUtils";

const AddPlayerDialog = ({ open, close, eventId }) => {
  const [searchVal, setSearchVal] = useState("");
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);

  // selectedUserId tracks whether the form was populated from a search result.
  // If set, we add the existing user; if null, we create a new one.
  const [selectedUserId, setSelectedUserId] = useState(null);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setSearchVal("");
    setUsers([]);
    setSelectedUserId(null);
    setFirstName("");
    setLastName("");
    setEmail("");
    setLoading(false);
    setSubmitting(false);
  };

  const handleClose = () => {
    reset();
    close();
  };

  const handleSearch = async () => {
    if (searchVal.trim().length < 3) return;
    setLoading(true);
    try {
      const result = await request("users/search", "post", {
        value: searchVal.trim(),
      });
      if (result.success) {
        setUsers(result.users || []);
      } else {
        toast.error(result.error || "Search failed");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSelectUser = (u) => {
    setSelectedUserId(u._id);
    setFirstName(u.profile?.firstName || "");
    setLastName(u.profile?.lastName || "");
    setEmail(u.email || "");
    // Clear results so the form section is the user's focus.
    setUsers([]);
    setSearchVal("");
  };

  // Editing any field after a search-select means we're no longer working
  // against the existing user record — switch back to "create new".
  const handleFieldChange = (setter) => (e) => {
    setter(e.target.value);
    if (selectedUserId) setSelectedUserId(null);
  };

  const handleSubmit = async () => {
    if (!eventId) return;
    setSubmitting(true);
    try {
      let body;
      let url;
      if (selectedUserId) {
        url = "events/addattendee";
        body = { userId: selectedUserId, eventId };
      } else {
        if (!firstName.trim() || !lastName.trim() || !email.trim()) {
          toast.error("First name, last name, and email are required.");
          return;
        }
        url = "events/createandadd";
        body = {
          userData: {
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            email: email.trim(),
          },
          eventId,
        };
      }
      const result = await request(url, "post", body);
      if (result?.success) {
        toast.success("Player added");
        handleClose();
      } else {
        toast.error(result?.error || "Failed to add player");
      }
    } catch (e) {
      toast.error(e?.message || "Failed to add player");
    } finally {
      setSubmitting(false);
    }
  };

  const submitDisabled =
    submitting ||
    (!selectedUserId &&
      (!firstName.trim() || !lastName.trim() || !email.trim()));

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Player</DialogTitle>
      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          overflow: "visible",
        }}
      >
        <Typography variant="h6">Search Existing User</Typography>
        <Typography variant="body2" color="text.secondary">
          Search by email. Select a result to populate the fields below.
        </Typography>

        <FormControl variant="filled" sx={{ mt: 1 }}>
          <InputLabel>Email</InputLabel>
          <FilledInput
            type="text"
            value={searchVal}
            onChange={(e) => setSearchVal(e.target.value)}
            endAdornment={
              <InputAdornment position="end">
                <IconButton onClick={handleSearch}>
                  <SearchIcon />
                </IconButton>
              </InputAdornment>
            }
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleSearch();
              }
            }}
          />
        </FormControl>

        {loading ? (
          <div
            style={{
              padding: "20px",
              width: "100%",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <CircularProgress />
          </div>
        ) : users.length > 0 ? (
          <List
            sx={{
              width: "100%",
              bgcolor: "background.paper",
              maxHeight: "40vh",
              overflowY: "auto",
              WebkitOverflowScrolling: "touch",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              p: 0,
            }}
          >
            {users.map((u, i) => (
              <ListItem key={`${i}-${u._id}`} disablePadding>
                <ListItemButton onClick={() => handleSelectUser(u)}>
                  <span style={{ flex: 1 }}>
                    {(u.profile?.firstName || u.profile?.lastName) && (
                      <strong>
                        {u.profile?.firstName} {u.profile?.lastName}
                        {" — "}
                      </strong>
                    )}
                    {u.email}
                  </span>
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        ) : null}

        <Typography variant="h6" sx={{ mt: 3 }}>
          Player Details
        </Typography>
        {selectedUserId && (
          <Typography variant="caption" color="success.main">
            An existing user is selected. Editing any field will switch to
            creating a new user.
          </Typography>
        )}
        <TextField
          label="First Name"
          value={firstName}
          onChange={handleFieldChange(setFirstName)}
          fullWidth
        />
        <TextField
          label="Last Name"
          value={lastName}
          onChange={handleFieldChange(setLastName)}
          fullWidth
        />
        <TextField
          label="Email"
          value={email}
          onChange={handleFieldChange(setEmail)}
          fullWidth
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitDisabled}
          startIcon={
            submitting ? <CircularProgress size={16} color="inherit" /> : null
          }
        >
          {submitting ? "Adding…" : "Add Player"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddPlayerDialog;
